import { Icon } from '@synergy/icons';
import { BasicTable } from '@synergy/table';
import { Col, Flex, Row } from 'antd';
import i18next from 'i18next';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '../../contexts/AuthContext';
import query from '../../server/constants.api';
import logger from '../../server/server';
import useFetch from '../../server/useFetch';
import { Field, PageContent, PageTitle } from '../../styles/global/Pages.styled';
import Theme from '../../styles/Theme/Theme';
import { REACT_APP_COMMAND_INCIDENT_URL } from '../../utils/config/configs';
import { base64ToBytes } from '../../utils/hooks/base64ToBytes';
import { toSnakeCase } from '../../utils/hooks/stringSerialization';
import { Button } from '../Global/Atomics/Button';
import Spinner from '../Global/Atomics/Spinner';
import { Text } from '../Global/Atomics/Text';
import { RegistryGlobalSubTabs } from '../Global/Organism/RegisterDetail/RegisteryGlobalSubTabs';

export const RegisterDetailView = ({ selectedPOI, setCreateIncidentModal, handle3DView, redirectToDigitalTwin }) => {
    const { t } = useTranslation();
    const { user, tenant } = useAuthContext();
    const zonesJSON = localStorage.getItem('unified_nova_zones');
    const zones = JSON.parse(zonesJSON);
    const [filteredBuilding, setFilteredBuilding] = useState(null);
    const [showMore, setShowMore] = useState(false);

    //Fetch aos dados
    const {
        data: incidentsBuildingDrawerData,
        refetch: refetchIncidentsBuildingDrawerData,
        remove: removeIncidentsBuildingDrawerData,
    } = useFetch(
        "/incidents/services/map",
        "incidentsBuildingDrawerData",
        {
            id: selectedPOI?.id,
            tenant: selectedPOI?.tenant
        },
        false,
        null,
        query.middlewareCommandURL
    );

    useEffect(() => {
        if (selectedPOI && selectedPOI?.label === "Item") {
            refetchIncidentsBuildingDrawerData();
        }
    }, [selectedPOI]);

    useEffect(() => {
        return () => {
            removeIncidentsBuildingDrawerData();
        };
    }, []);

    useEffect(() => {
        try {
            if (selectedPOI) {
                const keysToKeep = getKeysToKeep(selectedPOI);

                const filtered = Object.keys(selectedPOI)
                    .filter(key => keysToKeep.some(item => typeof item === 'string' ? item === key : item.fieldName === key))
                    .sort((a, b) => {
                        const indexA = keysToKeep.findIndex(item => typeof item === 'string' ? item === a : item.fieldName === a);
                        const indexB = keysToKeep.findIndex(item => typeof item === 'string' ? item === b : item.fieldName === b);
                        return indexA - indexB;
                    })
                    .reduce((obj, key) => {
                        const keyConfig = keysToKeep.find(item => typeof item === 'object' && item.fieldName === key);

                        if (keyConfig) {
                            if (keyConfig?.type === "date" && selectedPOI[key]) {
                                obj[key] = new Date(selectedPOI[key] * 1000).toLocaleString().replace(/:\d{2}$/, '');
                            } else {
                                obj[key] = selectedPOI[keyConfig.keyName || key];
                            }
                        } else {
                            obj[key] = selectedPOI[key];
                        }

                        if (key === 'description' && selectedPOI[key]) {
                            obj[key] = base64ToBytes(selectedPOI[key]);
                        }

                        return obj;
                    }, {});

                setFilteredBuilding(filtered);
            }
        } catch (error) {
            console.error('(RegisterDetailView page) Error in useEffect setting registryDetailData:', error);
            //reportError(error); // Send error report to backend
            logger.error('(RegisterDetailView page)  User: ' + user + ' Tenant: ' + tenant + ', Error in useEffect setting registryDetailData:', error)
        }
    }, [selectedPOI]);

    /**
     * Returns an array of keys to keep based on the selected point of interest (POI).
     *
     * @param {Object} selectedPOI - The selected point of interest.
     * @returns {Array} - An array of keys to keep.
     */
    const getKeysToKeep = (selectedPOI) => {
        if (!selectedPOI) return [];

        let keysToKeep;
        if (selectedPOI.label === "Incident") {
            keysToKeep = [
                "name", "status", "latitude", "longitude", "priority",
                { fieldName: "location", keyName: "serviceName" },
                { fieldName: "openDate", type: "date" },
                { fieldName: "lastUpdate", type: "date" },
                "category", "class",
                { fieldName: "description", span: 24 }
            ]
        } else if (selectedPOI.label === "Event") {
            keysToKeep = ["severity", "status", "type", "source", "latitude", "longitude", "class", "installationPoint",
                { fieldName: "description", span: 24 }]
        } else {
            keysToKeep = [
                "name", "address", "location", "class", "latitude", "longitude"
            ];
        }

        return keysToKeep;
    }

    /**
     * Returns the column span for a given field key.
     * @param {string} key - The field key.
     * @returns {number} - The column span for the field.
     */
    const getFieldColSpan = (key) => {
        const keysToKeep = getKeysToKeep(selectedPOI);
        const keyConfig = keysToKeep.find(item => typeof item === 'object' && item.fieldName === key);
        return keyConfig?.span || 12;
    }

    return (
        <>
            {filteredBuilding ?
                <PageContent id={filteredBuilding?.id} $backgroundColor='white'>
                    <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
                        <Col span={12}>
                            {selectedPOI?.has3D ?
                                <Button $backgroundColor={Theme.color['blue-4']} size='xs' $color='white' width='100%' onClick={() => handle3DView()}>{t('View 3D')}</Button>
                                : null}
                        </Col>
                        <Col span={12}>
                            {selectedPOI && selectedPOI?.label === 'Item' ?
                                <Button $backgroundColor={Theme.color['white']} size='xs' $color={Theme.color['blue-4']} border={`1px solid ${Theme.color['blue-4']}`}
                                    width='100%' onClick={() => redirectToDigitalTwin(selectedPOI)}>Digital Twin</Button>
                                : null}
                        </Col>
                    </Row>
                    <Row gutter={[32, 32]}>
                        <Col span={24}>
                            <Row gutter={[16, 16]}>
                                {Object.keys(filteredBuilding)?.map((key, index) => {
                                    if (showMore ? true : index < 6) {

                                        const colSpan = getFieldColSpan(key);

                                        return (
                                            <Col span={colSpan} key={index} className='d-flex'>
                                                <Field id={index} $backgroundColor="neutral-3" className='full-width'>
                                                    <Text $fontColor='coolgray-10' $fontSize='text_xs' $weight='regular'>
                                                        {t(`input.${toSnakeCase(key)}`)}</Text>
                                                    <Text $fontColor='coolgray-12' $fontSize='text_md' $weight='regular'>{filteredBuilding[key] && filteredBuilding[key] != '' ? filteredBuilding[key] : '-'}</Text>
                                                </Field>
                                            </Col>
                                        )
                                    }
                                })}
                                {Object.keys(filteredBuilding)?.length > 6 ?
                                    <Col span={24} >
                                        <Flex align="center" justify="center">
                                            <Flex align="center" justify="center" onClick={() => setShowMore(!showMore)} style={{ cursor: 'pointer' }}>
                                                <Text $fontColor='blue-4' $fontSize='text_md' $weight='medium' $marginRight="s03">{showMore ? t('seeLess') : t('seeMore')}</Text>
                                                <Icon icon={showMore ? 'ChevronUp' : 'ChevronDown'} iconsize='sm' color='blue-4' />
                                            </Flex>
                                        </Flex>
                                    </Col> : null
                                }
                            </Row>
                        </Col>
                    </Row>

                    {/* Quick Access */}
                    {selectedPOI?.label !== "Incident" && selectedPOI?.label !== "Event" && zones?.includes('registry') ?
                        <Row gutter={[32, 32]} style={{ marginTop: '40px' }}>
                            <Col span={24}>
                                <PageTitle>
                                    <Text $fontColor='coolgray-12' $fontSize='text_md' $weight='medium' $marginRight='s03'>{t('Quick Access')}</Text>
                                </PageTitle>
                            </Col>
                            <Col span={24}>
                                <Row gutter={[16, 16]}>
                                    {RegistryGlobalSubTabs.map((x, index) => {
                                        return (
                                            <Col span={(RegistryGlobalSubTabs.length - 1 === index && index % 2 === 0) ? 24 : 12} key={index}>
                                                <Flex align="center" justify="center">
                                                    <Button $backgroundColor={Theme.color['neutral-3']} size='xs' $color='black' width='100%'
                                                        onClick={() => window.location.href = '/registry/item/' + selectedPOI?.id + "/" + x.path}>
                                                        {t(x.name)}
                                                    </Button>
                                                </Flex>
                                            </Col>
                                        )
                                    })}
                                </Row>
                            </Col>
                        </Row>
                        : null}

                    {selectedPOI?.label === "Incident" ?
                        <Row gutter={[32, 32]} style={{ marginTop: '40px' }}>
                            <Col span={24}>
                                <Button size='xxs' $backgroundColor={Theme.color['blue-4']}
                                    onClick={() => window.open(`${REACT_APP_COMMAND_INCIDENT_URL}${selectedPOI.id}`, '_blank')}
                                >Manage Incident</Button>
                            </Col>
                        </Row>
                        : null}

                    {selectedPOI?.label !== "Incident" && selectedPOI?.label !== "Event" ?
                        <Row gutter={[32, 32]} style={{ marginTop: '40px' }}>
                            <Col span={24}>
                                <PageTitle>
                                    <Text $fontColor='coolgray-12' $fontSize='text_md' $weight='medium' $marginRight='s03'>{t('Incidents')}</Text>
                                </PageTitle>
                            </Col>
                            <Col span={24}>
                                <Button $backgroundColor='white' size='xs' $color={Theme.color['blue-4']} border={`1px solid ${Theme.color['blue-4']}`}
                                    width='100%' onClick={() => setCreateIncidentModal(true)}>{t('button.create_incident')}</Button>
                            </Col>
                            <Col span={24}>
                                <BasicTable tableConfiguration={tableConfiguration(t)} language={i18next.resolvedLanguage} fetchData={
                                    incidentsBuildingDrawerData ?
                                        incidentsBuildingDrawerData?.map((x, index) => {
                                            x.openDate = x.openDate.replace("T", " ");
                                            return { ...x, description: base64ToBytes(x.description), key: index };
                                        }) :
                                        []
                                } hrefLink={null} />
                            </Col>
                        </Row>
                        : null}

                    {/* <Row style={{ marginTop: '40px', display: 'flex', justifyContent: 'flex-end' }}>
                        <Col>
                            <a href={'/registry/item/' + selectedPOI?.id}>{t('seeDetail')}</a>
                        </Col>
                    </Row> */}
                </PageContent >
                : <div style={{ height: 'inherit', width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}><Spinner stroke="green" /></div>
            }
        </>
    );
};

const tableConfiguration = (t) => {
    return [{
        title: t('Creation Date'),
        dataIndex: "openDate"
    },
    {
        title: t('Category'),
        dataIndex: "category",
    },
    {
        title: t('Incident Description'),
        dataIndex: "description",
        width: '50%'
    },
    {
        title: t('Severity'),
        dataIndex: "priority"
    }];
};